import React, { Fragment } from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import useEventTracking from '@hooks/useEventTracking';
import { Company } from '@interfaces/ti50';
import { project_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { parameterize } from '@utils/formatting';
import { getImgixSrc } from '@utils/images';

import CompanyDetails from './CompanyDetails';

interface CompanyTableProps {
  category: string;
  companies: Company[];
  projectSlug: string;
  TI50Year: number;
}

const CompanyTable = ({ category, companies, projectSlug, TI50Year }: CompanyTableProps) => {
  const { trackEvent } = useEventTracking();

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.currentTarget;
    target.classList.toggle('selected-row');
    const tooltip = target.querySelector('.spnTooltip') as HTMLElement;
    if (tooltip) {
      tooltip.classList.toggle('hide');
    }
  };

  const handleTableClick = (event: React.MouseEvent<HTMLTableElement>) => {
    // Don't close the detail drop down if click event was by company link
    if ((event.target as HTMLElement).tagName === 'A') {
      return;
    }
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const closestRow = target.closest('tr');
    const nextRow = closestRow?.nextElementSibling as HTMLElement;

    if (nextRow) {
      const moreCompanyDetails = nextRow.querySelector('div.more-company-details') as HTMLElement;
      const detailCell = nextRow.querySelector("td[colspan='4']") as HTMLElement;

      if (moreCompanyDetails?.getAttribute('data-slide-open')) {
        moreCompanyDetails.style.display = 'none';
        moreCompanyDetails.removeAttribute('data-slide-open');
        detailCell?.classList.remove('show-detail-border');
      } else {
        moreCompanyDetails.style.display = 'block';
        moreCompanyDetails.setAttribute('data-slide-open', 'true');
        detailCell?.classList.add('show-detail-border');

        trackEvent(
          click({
            clickText: closestRow?.getAttribute('data-company'),
            clickTarget: ClickTarget.TI50Company,
          })
        );
      }
    }
  };

  return (
    <table
      className="company-table"
      cellSpacing={0}
      cellPadding={0}
      border={0}
      onClick={handleTableClick}
    >
      <caption className="hidden">
        Top {category} Startups in {TI50Year}
      </caption>
      <thead>
        <tr className="header-row">
          <th className="header-rank">Rank</th>
          <th className="header-empty">&nbsp;</th>
          <th className="header-name">Name</th>
          <th className="header-money">Valuation</th>
          <th className="header-location">Location</th>
        </tr>
      </thead>
      <tbody>
        {companies?.map((company) => (
          <Fragment key={company.company_name}>
            <tr
              className="data-row"
              data-company={`${company.company_name.replaceAll(/[.()-]/g, '')}`}
              onClick={handleRowClick}
            >
              <td className="data-rank">{company.sector_rank}</td>
              <td className="data-empty"></td>
              <td className="data-name">
                <div className="image-container">
                  {company.logo_url ? (
                    <LazyImgixImage
                      className="w-[50px] rounded-full"
                      src={getImgixSrc({
                        path: `${project_path(projectSlug)}/company-logos/${parameterize(
                          company.company_name.replaceAll(/[\s+.()-]/g, '')
                        )}.png`,
                        isEnvDependent: false,
                      })}
                    />
                  ) : (
                    <div className="logo-holder"></div>
                  )}
                </div>
                <h3 className="name-container">{company.company_name}</h3>
                <span className="spnTooltip">Click here for more information</span>
              </td>
              <td className="data-money">{company.valuation_value}</td>
              <td className="data-location">{company.location}</td>
            </tr>
            <tr className="more-info">
              <td className="info-empty"></td>
              <td className="info-empty"></td>
              <td colSpan={4} className="info-detail">
                <CompanyDetails company={company} />
              </td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  );
};

export default CompanyTable;
